import { Wand2 } from "lucide-react";
import { useMemo, useState } from "react";
import { contrastRatio, fixContrast, grade, lightnessDelta } from "@/color/contrast";
import { type ColorId, hexOf } from "@/model/resolve";
import { GROUPS, TOKENS, type TokenDef } from "@/model/tokens";
import { useEditor } from "@/store/editor";
import { GradeBadge } from "@/ui/controls/badges";
import { Button } from "@/ui/kit/button";
import { cn } from "@/ui/kit/cn";
import { Segmented } from "@/ui/kit/segmented";

export interface ContrastRow {
  def: TokenDef;
  fg: string;
  bg: string;
  ratio: number;
  min: number;
  dl: number;
  grade: ReturnType<typeof grade>;
}

export function useContrastRows(): ContrastRow[] {
  const resolved = useEditor((s) => s.resolved);
  const thresholds = useEditor((s) => s.thresholds);
  return useMemo(
    () =>
      TOKENS.filter((t) => t.contrast).map((def) => {
        const fg = hexOf(resolved, def.id);
        const bg = hexOf(resolved, def.contrast?.against as ColorId);
        const ratio = contrastRatio(fg, bg);
        const min = thresholds[def.contrast?.level ?? "text"];
        return { def, fg, bg, ratio, min, dl: lightnessDelta(fg, bg), grade: grade(ratio, min) };
      }),
    [resolved, thresholds],
  );
}

export function ContrastView() {
  const rows = useContrastRows();
  const thresholds = useEditor((s) => s.thresholds);
  const set = useEditor((s) => s.set);
  const select = useEditor((s) => s.select);
  const edit = useEditor((s) => s.edit);
  const [filter, setFilter] = useState<"issues" | "all">("issues");
  const shown = rows.filter((r) => filter === "all" || r.grade !== "pass");
  const counts = {
    fail: rows.filter((r) => r.grade === "fail").length,
    close: rows.filter((r) => r.grade === "close").length,
  };

  const fixAll = () =>
    edit((t) => {
      const overrides = { ...t.overrides };
      for (const r of rows)
        if (r.grade !== "pass")
          overrides[r.def.id] = fixContrast(r.fg as `#${string}`, r.bg as `#${string}`, r.min);
      return { ...t, overrides };
    });

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden rounded-xl border border-line bg-panel p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="font-semibold">Contrast</div>
          <div className="text-muted text-xs">
            WCAG 2 ratio of each token against the background it is drawn on. ΔL is the OKLCH
            lightness gap.
          </div>
        </div>
        <span className="flex-1" />
        <span className="text-xs">
          <span className="text-danger">{counts.fail} failing</span> ·{" "}
          <span className="text-warn">{counts.close} close</span> · {rows.length} checked
        </span>
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { value: "issues", label: "Issues" },
            { value: "all", label: "All" },
          ]}
        />
        <Button
          size="sm"
          variant="primary"
          disabled={counts.fail + counts.close === 0}
          onClick={fixAll}
        >
          <Wand2 /> Fix all
        </Button>
      </div>
      <div className="flex flex-wrap gap-4 text-muted text-xs">
        {(["text", "secondary", "decorative"] as const).map((level) => (
          <label key={level} className="flex items-center gap-2">
            <span className="capitalize">{level}</span>
            <input
              type="number"
              step={0.5}
              min={1}
              max={21}
              value={thresholds[level]}
              onChange={(e) =>
                set({ thresholds: { ...thresholds, [level]: Number(e.target.value) || 1 } })
              }
              className="h-7 w-16 rounded-md border border-line bg-bg px-2 text-fg"
            />
            <span>: 1</span>
          </label>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-line">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-raised text-left text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Token</th>
              <th className="px-3 py-2 font-medium">Sample</th>
              <th className="px-3 py-2 font-medium">Ratio</th>
              <th className="px-3 py-2 font-medium">ΔL</th>
              <th className="px-3 py-2 font-medium">Level</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr
                key={r.def.id}
                className="cursor-pointer border-line border-t hover:bg-raised/60"
                onClick={() => select({ kind: "token", id: r.def.id })}
              >
                <td className="px-3 py-1.5">
                  <div className="text-fg">{r.def.label}</div>
                  <div className="text-faint">
                    {GROUPS.find((g) => g.id === r.def.group)?.label} · {r.def.section}
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  <span
                    className="rounded px-2 py-0.5 font-mono"
                    style={{ background: r.bg, color: r.fg }}
                  >
                    Aa sample
                  </span>
                </td>
                <td className={cn("px-3 py-1.5 font-mono tabular-nums")}>{r.ratio.toFixed(2)}</td>
                <td className="px-3 py-1.5 font-mono text-muted tabular-nums">{r.dl.toFixed(0)}</td>
                <td className="px-3 py-1.5">
                  <GradeBadge grade={r.grade} label={`${r.def.contrast?.level} ≥ ${r.min}`} />
                </td>
                <td className="px-3 py-1.5 text-right">
                  {r.grade !== "pass" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        edit((t) => ({
                          ...t,
                          overrides: {
                            ...t.overrides,
                            [r.def.id]: fixContrast(
                              r.fg as `#${string}`,
                              r.bg as `#${string}`,
                              r.min,
                            ),
                          },
                        }));
                      }}
                    >
                      <Wand2 /> Fix
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-muted">
                  Every checked token meets its threshold.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
